// Copyright 2025, University of Maryland, All Rights Reserved
import { useCallback, useMemo, useState } from 'react'
import {
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  closestCorners,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import type { ActiveDragItem, BuilderWidget } from '../types'

interface UseWidgetDnDOptions {
  addWidgetFromNode: (tabId: string, nodeId: string) => void
  moveWidget: (sourceTabId: string, targetTabId: string, widget: BuilderWidget, overId: string | null) => void
}

export const useWidgetDnD = ({ addWidgetFromNode, moveWidget }: UseWidgetDnDOptions) => {
  // Separate mouse/touch sensors: mouse drags start after a small movement so
  // clicks still fire; touch drags need a hold delay so scrolling stays usable.
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 8,
      },
    }),
  )

  const [activeDragItem, setActiveDragItem] = useState<ActiveDragItem | null>(null)

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const item = event.active.data.current as ActiveDragItem | undefined
    if (item) {
      setActiveDragItem(item)
    }
  }, [])

  const handleDragCancel = useCallback(() => {
    setActiveDragItem(null)
  }, [])

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { over } = event
    if (!activeDragItem || !over) {
      setActiveDragItem(null)
      return
    }

    const targetTabId = over.data.current?.tabId as string | undefined
    if (!targetTabId) {
      setActiveDragItem(null)
      return
    }

    if (activeDragItem.type === 'palette-node') {
      addWidgetFromNode(targetTabId, activeDragItem.nodeId)
    } else if (activeDragItem.type === 'builder-widget') {
      moveWidget(activeDragItem.tabId, targetTabId, activeDragItem.widget, over.id as string | null)
    }

    setActiveDragItem(null)
  }, [activeDragItem, addWidgetFromNode, moveWidget])

  const dndContextProps = useMemo(() => ({
    sensors,
    collisionDetection: closestCorners,
    onDragStart: handleDragStart,
    onDragEnd: handleDragEnd,
    onDragCancel: handleDragCancel,
  }), [handleDragCancel, handleDragEnd, handleDragStart, sensors])

  return {
    sensors,
    activeDragItem,
    dndContextProps,
  }
}
